import * as React from 'react'
import { Team } from 'store/modules/teams'
import headerStyles from 'src/design-system/components/organisms/HealthAudits/HealthAuditDrawerHeader.module.scss'
import drawerStyles from 'src/design-system/components/organisms/HealthAudits/HealthAuditDrawer.module.scss'
import contentStyles from 'src/design-system/components/organisms/HealthAudits/HealthAuditDrawerContent.module.scss'
import {
  X,
  UsersThree,
  User,
  CaretDown,
  CaretUp,
  PencilSimple,
} from '@phosphor-icons/react'
import { useModalContext } from 'src/design-system'
import {
  POSITION_MODAL_ID,
  PositionModalProps,
} from 'components/position-modal/utils'
import { SkillModalProps, SKILL_MODAL_ID } from 'components/skill-modal/utils'
import * as Collapsible from '@radix-ui/react-collapsible'
import { CircleDashed } from '@phosphor-icons/react'

type HealthDrawerContextType = {
  openDrawer: (team: Team) => void
  closeDrawer: () => void
  currentTeam?: Team
  isVisible: boolean
}

const HealthDrawerContext = React.createContext<HealthDrawerContextType | null>(
  null
)

export const HealthAuditDrawer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentTeam, setCurrentTeam] = React.useState<Team>()
  const [isVisible, setIsVisible] = React.useState(false)

  const openDrawer = (team: Team) => {
    setCurrentTeam(team)
    setIsVisible(true)
  }

  const closeDrawer = () => {
    setIsVisible(false)
    setTimeout(() => setCurrentTeam(undefined), 300) // Match animation duration
  }

  return (
    <HealthDrawerContext.Provider
      value={{
        openDrawer,
        closeDrawer,
        currentTeam,
        isVisible,
      }}
    >
      {children}
      {currentTeam && (
        <DrawerContent team={currentTeam} onClose={closeDrawer} />
      )}
    </HealthDrawerContext.Provider>
  )
}

const DrawerContent = ({
  team,
  onClose,
}: {
  team: Team
  onClose: () => void
}) => {
  const { isVisible } = useHealthAuditDrawer()
  const [issues, setIssues] = React.useState<{
    skill_issues: Array<{
      skill_id: number
      skill_name: string
      message: string
    }>
    position_issues: Array<{
      position_id: number
      position_name: string
      message: string
    }>
    team_issues: Array<{ message: string }>
  }>({ skill_issues: [], position_issues: [], team_issues: [] })
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const { openModal } = useModalContext()
  const [isSkillsOpen, setIsSkillsOpen] = React.useState(true)
  const [isPositionsOpen, setIsPositionsOpen] = React.useState(true)
  const [isTeamOpen, setIsTeamOpen] = React.useState(true)

  const fetchIssues = React.useCallback(async () => {
    try {
      const response = await fetch(`/api/v1/health_audits?team_id=${team.id}`)
      if (!response.ok) throw new Error('Failed to fetch')
      const data = await response.json()
      const existingIssues = data.existing_issues || {}
      setIssues({
        skill_issues: existingIssues.skill_issues || [],
        position_issues: existingIssues.position_issues || [],
        team_issues: existingIssues.team_issues || [],
      })
    } catch (err) {
      setError('Failed to load health audit data')
    } finally {
      setLoading(false)
    }
  }, [team.id])

  React.useEffect(() => {
    if (team?.id) {
      fetchIssues()
    }
  }, [team.id, fetchIssues])

  React.useEffect(() => {
    const handleHealthAuditUpdate = () => {
      fetchIssues()
    }

    document.addEventListener(
      'skillHealthAuditUpdated',
      handleHealthAuditUpdate
    )
    document.addEventListener(
      'positionHealthAuditUpdated',
      handleHealthAuditUpdate
    )

    return () => {
      document.removeEventListener(
        'skillHealthAuditUpdated',
        handleHealthAuditUpdate
      )
      document.removeEventListener(
        'positionHealthAuditUpdated',
        handleHealthAuditUpdate
      )
    }
  }, [team.id, fetchIssues])

  const groupIssues = React.useCallback(
    <T extends { message: string }>(issues: T[], idKey: keyof T) => {
      return issues.reduce((acc, issue) => {
        const key = String(issue[idKey])
        return {
          ...acc,
          [key]: {
            ...issue,
            messages: [...(acc[key]?.messages || []), issue.message],
          },
        }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }, {} as Record<string, any>)
    },
    []
  )

  const handleSkillClick = React.useCallback(
    (skillId: number) => {
      openModal<SkillModalProps>(SKILL_MODAL_ID, {
        skillId: String(skillId),
        source: 'health-audit-drawer',
      })
    },
    [openModal]
  )

  const handlePositionClick = React.useCallback(
    (positionId: number) => {
      openModal<PositionModalProps>(POSITION_MODAL_ID, {
        positionId: String(positionId),
        source: 'health-audit-drawer',
      })
    },
    [openModal]
  )

  const CollapsibleSection = ({
    title,
    count,
    isOpen,
    onOpenChange,
    children,
  }: {
    title: string
    count: number
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    children: React.ReactNode
  }) => (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={onOpenChange}
      className={contentStyles.section}
    >
      <div className={contentStyles.sectionTrigger}>
        <h4 className={contentStyles.sectionTitle}>
          {title}
          <span className={contentStyles.countBadge}>{count}</span>
        </h4>
        <Collapsible.Trigger className="ml-auto">
          {isOpen ? <CaretUp weight="bold" /> : <CaretDown weight="bold" />}
        </Collapsible.Trigger>
      </div>
      <Collapsible.Content>{children}</Collapsible.Content>
    </Collapsible.Root>
  )

  const hasIssues =
    issues.skill_issues.length > 0 ||
    issues.position_issues.length > 0 ||
    issues.team_issues.length > 0

  return (
    <div className={drawerStyles.drawer} data-visible={isVisible}>
      <div className={drawerStyles.contentWrapper}>
        {/* Header */}
        <div className={headerStyles.header}>
          <h3 className={headerStyles.title}>{team.name}</h3>
          <button onClick={onClose} className={headerStyles.closeButton}>
            <X weight="bold" className={headerStyles.closeIcon} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className={contentStyles.scrollContent}>
          {loading ? (
            <div className={contentStyles.loading}>Loading issues...</div>
          ) : error ? (
            <div className={contentStyles.error}>{error}</div>
          ) : hasIssues ? (
            <div className={contentStyles.sectionContent}>
              {/* Skills Issues Section */}
              {issues.skill_issues.length > 0 && (
                <CollapsibleSection
                  title="Skills"
                  count={issues.skill_issues.length}
                  isOpen={isSkillsOpen}
                  onOpenChange={setIsSkillsOpen}
                >
                  <div>
                    {Object.values(
                      groupIssues(issues.skill_issues, 'skill_id')
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ).map((group: any, index) => (
                      <div key={index} className={contentStyles.issueItem}>
                        <div className={contentStyles.issueTitle}>
                          <div className="flex items-center justify-between gap-3">
                            <span>{group.skill_name}</span>
                            <div className={contentStyles.meta}>
                              <div className="flex items-center gap-1">
                                <UsersThree weight="bold" className="w-4 h-4" />
                                <span>{group.team_count}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <User weight="bold" className="w-4 h-3" />
                                <span>{group.user_count}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleSkillClick(group.skill_id)}
                            >
                              <PencilSimple
                                className="w-4 h-4 text-gray-700"
                                weight="bold"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}

              {/* Positions Issues Section */}
              {issues.position_issues.length > 0 && (
                <CollapsibleSection
                  title="Positions"
                  count={issues.position_issues.length}
                  isOpen={isPositionsOpen}
                  onOpenChange={setIsPositionsOpen}
                >
                  <div>
                    {Object.values(
                      groupIssues(issues.position_issues, 'position_id')
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ).map((group: any, index) => (
                      <div key={index} className={contentStyles.issueItem}>
                        <div className={contentStyles.issueTitle}>
                          <div className="flex items-center justify-between gap-3">
                            <span>{group.position_name}</span>
                            <div className={contentStyles.meta}>
                              <User
                                weight="bold"
                                className={contentStyles.icon}
                              />
                              <span>{group.user_count}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                handlePositionClick(group.position_id)
                              }
                            >
                              <PencilSimple
                                className="w-4 h-4 text-gray-700"
                                weight="bold"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}

              {/* Team Issues Section */}
              {issues.team_issues.length > 0 && (
                <CollapsibleSection
                  title="Team"
                  count={issues.team_issues.length}
                  isOpen={isTeamOpen}
                  onOpenChange={setIsTeamOpen}
                >
                  <div>
                    {issues.team_issues.map((issue, index) => (
                      <div key={index} className={contentStyles.issueItem}>
                        <div className={contentStyles.issueTitle}>
                          {issue.message}
                        </div>
                        <div className={contentStyles.meta}>
                          <span>Team-wide issue</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}
            </div>
          ) : (
            <div className={contentStyles.noIssues}>
              <CircleDashed className={contentStyles.iconSize} />
              <div>No issues found for {team.name}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const useHealthAuditDrawer = () => {
  const context = React.useContext(HealthDrawerContext)
  if (!context)
    throw new Error(
      'useHealthAuditDrawer must be used within HealthAuditDrawer'
    )
  return context
}
